import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Square, Play, Pause, Download, Share2, Heart, Sparkles, Loader2, Lock, Unlock, RotateCcw, Radio, Swords, Crown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { voicePresets, voiceOfTheDay, voiceFavorites, voiceFavToggle, renderWithEffect, voiceUnlocks, voiceUnlock, startLiveChain, voiceOfTheWeek } from "@/lib/voiceApi";

/**
 * Jixxeo Voiceification — single-page voice changer.
 * Records mic → applies chosen preset via Web Audio → plays / downloads / shares.
 * Zero third-party mention: everything is "Jixxeo Voiceification".
 */
export default function VoiceificationApp() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [presets, setPresets] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("All");
  const [vod, setVod] = useState(null);
  const [favs, setFavs] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [recording, setRecording] = useState(false);
  const [srcBlob, setSrcBlob] = useState(null);
  const [outUrl, setOutUrl] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [unlocked, setUnlocked] = useState(new Set());
  const [liveOn, setLiveOn] = useState(false);
  const [votw, setVotw] = useState(null);
  const liveRef = useRef(null);
  const liveAudioRef = useRef(null);
  const mediaRef = useRef(null); const chunksRef = useRef([]); const streamRef = useRef(null);
  const audioRef = useRef(null); const timerRef = useRef(null);

  useEffect(() => {
    document.body.classList.add("voice-scope");
    return () => { document.body.classList.remove("voice-scope"); if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); };
  }, []);
  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/voiceification"); return; }
    voicePresets().then((r) => { setPresets(r.presets || []); setCats(r.categories || []); });
    voiceOfTheDay().then((r) => { setVod(r.preset); if (!selected) setSelected(r.preset); }).catch(() => {});
    voiceFavorites().then((r) => setFavs(new Set(r.favorites || []))).catch(() => {});
    voiceUnlocks().then((r) => setUnlocked(new Set(r.unlocked || []))).catch(() => {});
    voiceOfTheWeek().then((r) => setVotw(r)).catch(() => {});
  }, [user, loading, nav]);   // eslint-disable-line

  // Toggle live-mode when user taps Live button
  const toggleLive = async () => {
    if (liveOn) { liveRef.current?.stop?.(); liveRef.current = null; setLiveOn(false); return; }
    if (!selected) { toast.error("Pick an effect first"); return; }
    if (selected.premium && !unlocked.has(selected.slug)) { toast.error("Unlock this voice with coins first"); return; }
    setLiveOn(true);
    liveRef.current = await startLiveChain(selected.cfg || {}, {
      onStream: (stream) => {
        if (liveAudioRef.current) {
          liveAudioRef.current.srcObject = stream;
          liveAudioRef.current.play().catch(() => {});
        }
      },
      onError: () => { toast.error("Mic blocked"); setLiveOn(false); },
    });
  };

  const doUnlock = async (preset) => {
    if (!window.confirm(`Unlock "${preset.name}" for 50 coins?`)) return;
    try {
      const r = await voiceUnlock(preset.slug);
      setUnlocked((s) => new Set([...s, preset.slug]));
      toast.success(`Unlocked! ${r.coins_left != null ? `${r.coins_left} coins left.` : ""}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 402) toast.error("Not enough coins — grab a pack from the coin store.");
      else toast.error(typeof d === "string" ? d : "Couldn't unlock");
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        setSrcBlob(blob);
        stream.getTracks().forEach((t) => t.stop()); streamRef.current = null;
        if (selected) applyEffect(blob, selected);
      };
      mr.start();
      mediaRef.current = mr; setRecording(true); setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => {
        if (s + 1 >= 30) { stopRec(); return s + 1; }
        return s + 1;
      }), 1000);
    } catch { toast.error("Microphone blocked — check browser permissions"); }
  };
  const stopRec = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    setRecording(false); if (timerRef.current) clearInterval(timerRef.current);
  };

  const applyEffect = async (blob, preset) => {
    setRendering(true);
    try {
      const out = await renderWithEffect(blob, preset.cfg || {});
      const url = URL.createObjectURL(out);
      setOutUrl(url);
      // auto-play preview
      setTimeout(() => audioRef.current?.play().catch(() => {}), 200);
    } catch (e) { toast.error("Rendering failed on this browser"); }
    finally { setRendering(false); }
  };

  const pickPreset = (p) => {
    if (p.premium && !unlocked.has(p.slug)) { doUnlock(p); return; }
    setSelected(p);
    // Refresh live chain to use new effect if live is on
    if (liveOn) { liveRef.current?.stop?.(); liveRef.current = null; setTimeout(() => toggleLive(), 60); }
    if (srcBlob) applyEffect(srcBlob, p);
  };

  const download = async () => {
    if (!outUrl || !selected) return;
    const a = document.createElement("a");
    a.href = outUrl;
    a.download = `jixxeo-${selected.slug}-${Date.now()}.wav`;
    a.click();
    toast.success("Saved — share it with #JixxeoVoiceification");
  };

  const share = async () => {
    if (!outUrl || !selected) return;
    try {
      const res = await fetch(outUrl); const blob = await res.blob();
      const file = new File([blob], `jixxeo-${selected.slug}.wav`, { type: "audio/wav" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Made with Jixxeo Voiceification", text: "🔊 Made with Jixxeo Voiceification" });
      } else { download(); }
    } catch { /* user cancelled */ }
  };

  const toggleFav = async (slug) => {
    try {
      const r = await voiceFavToggle(slug);
      setFavs((s) => { const n = new Set(s); r.favorited ? n.add(slug) : n.delete(slug); return n; });
    } catch { toast.error("Sign in to favorite"); }
  };

  const visible = cat === "All" ? presets : cat === "Favorites" ? presets.filter((p) => favs.has(p.slug)) : presets.filter((p) => p.category === cat);

  return (
    <div data-testid="voice-shell" className="min-h-screen bg-gradient-to-b from-[#1A0033] via-[#0A0018] to-black text-white pb-32">
      <style>{`body.voice-scope{background:#0A0018 !important; color:#fff !important} body.voice-scope .noise-overlay,body.voice-scope .grid-backdrop{display:none !important}`}</style>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0A0018]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FF00E5] to-[#7C3AED] flex items-center justify-center shadow-lg shadow-[#FF00E5]/40">
            <Mic className="w-5 h-5 text-white" fill="white" />
          </div>
          <div className="leading-tight">
            <div className="font-black text-[16px] tracking-tight">Jixxeo <span className="text-[#FF00E5]">Voiceification</span></div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-white/40">record · transform · share</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-4 space-y-5">
        {/* Voice of the Week — top creator across the last 7 days */}
        {votw?.winner && (
          <section data-testid="voice-of-week"
            className="rounded-2xl bg-gradient-to-br from-[#FFD700]/25 via-[#FF00E5]/15 to-transparent border border-[#FFD700]/50 p-4 relative overflow-hidden">
            <div className="inline-flex items-center gap-1.5 bg-black/40 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-2 border border-[#FFD700]/50 text-[#FFD700]">
              <Crown className="w-3 h-3" fill="currentColor" /> Voice of the week
            </div>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full flex items-center justify-center font-black text-xl border-2 border-[#FFD700] shadow-lg shadow-[#FFD700]/40"
                   style={{ background: votw.winner.avatar_color }}
                   data-testid="votw-avatar">
                {(votw.winner.user_name || "?")[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-lg truncate" data-testid="votw-name">{votw.winner.user_name || "A creator"}</div>
                <div className="text-xs text-white/70 truncate">
                  {votw.winner.total_votes} community vote{votw.winner.total_votes === 1 ? "" : "s"}
                  {votw.winner.submissions ? ` · ${votw.winner.submissions} battle${votw.winner.submissions === 1 ? "" : "s"}` : ""}
                </div>
                {votw.winner.top_prompt_text && (
                  <div className="text-[10px] uppercase tracking-widest text-white/50 mt-0.5 truncate">
                    Top take: "{votw.winner.top_prompt_text}"
                  </div>
                )}
              </div>
              <a href="/voiceification/battles" data-testid="votw-cta"
                className="shrink-0 inline-flex items-center gap-1 bg-[#FFD700] text-black font-black text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full">
                Beat them
              </a>
            </div>
          </section>
        )}

        {/* Voice of the Day */}
        {vod && (
          <section data-testid="voice-of-day" onClick={() => pickPreset(vod)}
            className="rounded-2xl bg-gradient-to-br from-[#FF00E5]/25 via-[#7C3AED]/20 to-transparent border border-[#FF00E5]/40 p-4 cursor-pointer hover:border-[#FF00E5] transition-colors">
            <div className="inline-flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-2"><Sparkles className="w-3 h-3" fill="currentColor" /> Voice of the day</div>
            <div className="flex items-center gap-3">
              <div className="text-4xl">{vod.emoji}</div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-xl">{vod.name}</div>
                <div className="text-xs text-white/60">{vod.category} — tap to try it, then record</div>
              </div>
            </div>
          </section>
        )}

        {/* Selected + big record button */}
        <section className="rounded-3xl bg-black/40 backdrop-blur border border-white/10 p-6 text-center">
          <div className="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-1">Now applying</div>
          <div className="text-3xl">{selected?.emoji || "🎙️"}</div>
          <div className="font-black text-xl mt-1" data-testid="voice-selected-name">{selected?.name || "Pick an effect"}</div>

          <button data-testid={recording ? "voice-stop" : "voice-record"}
            onClick={recording ? stopRec : startRec}
            className={"mt-5 w-24 h-24 rounded-full mx-auto flex items-center justify-center transition-all shadow-2xl " + (recording ? "bg-[#FF3B30] shadow-[#FF3B30]/50 animate-pulse" : "bg-gradient-to-br from-[#FF00E5] to-[#7C3AED] shadow-[#FF00E5]/40 hover:scale-105")}>
            {recording ? <Square className="w-10 h-10 text-white" fill="white" /> : <Mic className="w-10 h-10 text-white" fill="white" />}
          </button>
          <div className="mt-2 text-xs text-white/50">{recording ? `Recording… ${seconds}s (max 30s)` : "Tap to record"}</div>

          {/* Live mode + Battles quick actions */}
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button data-testid="voice-live-toggle" onClick={toggleLive}
              className={"inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full border transition-colors " + (liveOn ? "bg-[#39FF14] text-black border-[#39FF14]" : "bg-white/5 text-white border-white/15 hover:border-[#39FF14]")}>
              <Radio className={"w-3.5 h-3.5 " + (liveOn ? "animate-pulse" : "")} /> {liveOn ? "Live · streaming" : "Live mode"}
            </button>
            <a href="/voiceification/battles" data-testid="voice-battles-link"
              className="inline-flex items-center gap-1 bg-gradient-to-r from-[#FFD700]/20 to-transparent text-[#FFD700] border border-[#FFD700]/40 hover:border-[#FFD700] font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-full">
              <Swords className="w-3.5 h-3.5" /> Voice battles
            </a>
          </div>
          {liveOn && <audio ref={liveAudioRef} autoPlay className="hidden" />}

          {rendering && <div className="mt-3 inline-flex items-center gap-2 text-white/70 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Applying {selected?.name}…</div>}

          {outUrl && !rendering && (
            <div className="mt-4 space-y-3">
              <audio ref={audioRef} src={outUrl} controls className="w-full rounded" data-testid="voice-audio" />
              <div className="flex flex-wrap justify-center gap-2">
                <button data-testid="voice-download" onClick={download} className="inline-flex items-center gap-1 bg-white text-black font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-full"><Download className="w-3.5 h-3.5" /> Download</button>
                <button data-testid="voice-share" onClick={share} className="inline-flex items-center gap-1 bg-gradient-to-r from-[#FF00E5] to-[#7C3AED] text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-full"><Share2 className="w-3.5 h-3.5" /> Share</button>
                {srcBlob && selected && <button data-testid="voice-redo" onClick={() => applyEffect(srcBlob, selected)} className="inline-flex items-center gap-1 bg-white/10 text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-full"><RotateCcw className="w-3.5 h-3.5" /> Redo</button>}
              </div>
              <div className="text-[10px] text-white/40">Made with Jixxeo Voiceification — share the vibe.</div>
            </div>
          )}
        </section>

        {/* Category tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {["All", "Favorites", ...cats.map((c) => c.name)].map((c) => (
            <button key={c} data-testid={`voice-cat-${c}`} onClick={() => setCat(c)}
              className={"whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold " + (cat === c ? "bg-[#FF00E5] text-black" : "bg-white/5 border border-white/10 text-white/70 hover:border-[#FF00E5]/50")}>
              {c === "Favorites" ? `♥ ${c}` : c}
            </button>
          ))}
        </div>

        {/* Effect grid */}
        <section data-testid="voice-grid" className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {visible.map((p) => {
            const isSel = selected?.slug === p.slug;
            const isFav = favs.has(p.slug);
            const isUnlocked = unlocked.has(p.slug);
            const showLock = p.premium && !isUnlocked;
            return (
              <div key={p.slug} data-testid={`voice-tile-${p.slug}`}
                className={"relative rounded-2xl p-3 border transition-all cursor-pointer " + (isSel ? "border-[#FF00E5] bg-[#FF00E5]/15" : showLock ? "border-[#FFD700]/30 bg-[#FFD700]/5 hover:border-[#FFD700]" : "border-white/10 bg-white/5 hover:border-white/30")}
                onClick={() => pickPreset(p)}>
                <button aria-label="favorite" onClick={(e) => { e.stopPropagation(); toggleFav(p.slug); }}
                  className={"absolute top-1.5 right-1.5 p-1 rounded-full " + (isFav ? "text-[#FF00E5]" : "text-white/40 hover:text-white/80")} data-testid={`fav-${p.slug}`}>
                  <Heart className="w-3.5 h-3.5" fill={isFav ? "currentColor" : "none"} />
                </button>
                {p.premium && (isUnlocked
                  ? <Unlock className="w-3 h-3 absolute top-1.5 left-1.5 text-[#39FF14]" />
                  : <Lock className="w-3 h-3 absolute top-1.5 left-1.5 text-[#FFD700]" />)}
                <div className="text-3xl text-center">{p.emoji}</div>
                <div className="font-black text-[11px] text-center mt-1 truncate">{p.name}</div>
                <div className="text-[9px] uppercase tracking-widest text-white/40 text-center">{p.category}</div>
              </div>
            );
          })}
          {visible.length === 0 && <div className="col-span-full text-center text-white/50 text-sm py-8">Nothing to show — add favorites to see them here.</div>}
        </section>

        {/* Coin shop teaser */}
        <section data-testid="voice-shop" className="rounded-2xl bg-gradient-to-br from-[#FFD700]/15 to-transparent border border-[#FFD700]/30 p-4 text-center">
          <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#FFD700] mb-1"><Lock className="w-3 h-3" /> Coin shop</div>
          <div className="font-black text-lg">Unlock premium voices — 50 coins each</div>
          <div className="text-xs text-white/60 mb-2">Demon · Alien · Space · Dragon · Dark Lord · Clown · Glitch. Tap any locked tile above to unlock instantly.</div>
          <a href="/coins" className="inline-flex items-center gap-1 bg-[#FFD700] text-black font-black text-xs uppercase tracking-wider px-4 py-2 rounded-full">Get coins</a>
        </section>
      </main>
    </div>
  );
}
